import axios from 'axios';
import type {
  FileInfo,
  ColumnInfo,
  TopologyResult,
  AnalysisRequest,
  AnalysisResult,
  TemplateInfo,
  ChartRecommendation,
  PreviewData,
  ChartConfig,
  FilterCondition,
} from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL
  || (typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? window.location.origin
    : 'http://localhost:8000');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Upload
export async function uploadFile(file: File): Promise<FileInfo> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<FileInfo>('/api/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// Data
export async function getPreview(fileId: string, sheet?: string): Promise<PreviewData> {
  const params = sheet ? { sheet } : {};
  const { data } = await api.get<PreviewData>(`/api/data/${fileId}/preview`, { params });
  return data;
}

export async function getColumns(fileId: string, sheet?: string): Promise<ColumnInfo[]> {
  const params = sheet ? { sheet } : {};
  const { data } = await api.get<ColumnInfo[]>(`/api/data/${fileId}/columns`, { params });
  return data;
}

export async function getSheets(fileId: string): Promise<{ sheets: string[] }> {
  const { data } = await api.get<{ sheets: string[] }>(`/api/data/${fileId}/sheets`);
  return data;
}

export async function filterData(
  fileId: string,
  filters: FilterCondition[],
  sheet?: string,
  removeOutliers?: boolean
): Promise<PreviewData> {
  const params: Record<string, any> = {};
  if (sheet) params.sheet = sheet;
  if (removeOutliers) params.remove_outliers = true;
  const { data } = await api.post<PreviewData>(
    `/api/data/${fileId}/filter`,
    { filters },
    { params }
  );
  return data;
}

export async function getTopology(fileId: string, columns?: string[]): Promise<TopologyResult> {
  const params: Record<string, string> = {};
  if (columns && columns.length > 0) params.columns = columns.join(',');
  const { data } = await api.get<TopologyResult>(`/api/data/${fileId}/topology`, { params });
  return data;
}

// Analysis
export async function runAnalysis(request: AnalysisRequest): Promise<AnalysisResult> {
  const { data } = await api.post<AnalysisResult>('/api/analyze', request);
  return data;
}

// DEV ONLY — synthetic test data per module (remove before prod)
export interface TestDataResponse {
  file_id: string;
  filename: string;
  rows: number;
  columns: number;
  column_names: string[];
  suggested: {
    columns: string[];
    sub_type: string | null;
    extra: Record<string, any>;
  };
}

export async function fetchTestData(moduleName: string): Promise<TestDataResponse> {
  const { data } = await api.post<TestDataResponse>('/api/test-data', { module_name: moduleName });
  return data;
}

// Templates
export async function getTemplates(): Promise<TemplateInfo[]> {
  const { data } = await api.get<TemplateInfo[]>('/api/templates');
  return data;
}

export async function getTemplate(type: string): Promise<TemplateInfo> {
  const { data } = await api.get<TemplateInfo>(`/api/templates/${type}`);
  return data;
}

export async function downloadTemplate(type: string, params: Record<string, any>): Promise<Blob> {
  const { data } = await api.get(`/api/templates/${type}/download`, {
    params,
    responseType: 'blob',
  });
  return data;
}

// Charts
export async function recommendCharts(
  fileId: string,
  columns: string[]
): Promise<any> {
  const { data } = await api.post('/api/charts/recommend', {
    file_id: fileId,
    columns,
  });
  return data;
}

export async function generateChart(
  fileId: string,
  chartType: string,
  columns: string[],
  options?: Record<string, any>
): Promise<ChartConfig> {
  const { data } = await api.post<ChartConfig>('/api/charts/generate', {
    file_id: fileId,
    chart_type: chartType,
    columns,
    options: options || {},
  });
  return data;
}

export default api;
